import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Tab, Tabs } from '@mui/material';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { canManageFinanceCost } from '../../utils/finance/financePermissions';
import {
  FINANCE_PRESET,
  istanbulDateKey,
  resolveCompareRange,
  resolvePresetRange
} from '../../utils/finance/financePeriod';
import { compareFinanceSummaries, isActiveExpense, summarizeFinancePeriod } from '../../utils/finance/financePnl';
import {
  addFinanceExpense,
  cancelFinanceExpense,
  loadFinanceExpenses,
  updateFinanceExpense
} from '../../utils/finance/financeExpenseFirestore';
import FinancePeriodSelector from './FinancePeriodSelector';
import FinanceOverview from './FinanceOverview';
import { getLiveHasRates } from '../../utils/finance/financeGoldRate';
import { loadFinanceCostSettings } from '../../utils/finance/financeCostFirestore';
import { buildFinanceSummaryPdf } from '../../utils/finance/financeSummaryPdf';
import FinanceExpenses from './FinanceExpenses';
import FinanceCostSettings from './FinanceCostSettings';
import FinanceDetailDrawer from './FinanceDetailDrawer';
import { fx } from './financeTheme';
import { usePriceContext } from '../../context/PriceContext';

async function loadIslemler(sube, range) {
  const snap = await getDocs(
    query(
      collection(db, 'subeler', sube, 'islemler'),
      where('timestamp', '>=', range.start),
      where('timestamp', '<=', range.end),
      orderBy('timestamp', 'desc')
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export default function FinansPage({ sube, activePersonel, branchAuthIsAdmin, preview }) {
  const canEdit = canManageFinanceCost(activePersonel, { branchAuthIsAdmin });
  const priceCtx = usePriceContext();
  const livePrices = useMemo(
    () => preview?.prices || priceCtx?.prices || [],
    [preview?.prices, priceCtx?.prices]
  );
  const [tab, setTab] = useState('ozet');
  const [preset, setPreset] = useState(FINANCE_PRESET.BUGUN);
  const [customStart, setCustomStart] = useState(istanbulDateKey(new Date()));
  const [customEnd, setCustomEnd] = useState(istanbulDateKey(new Date()));
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [islemler, setIslemler] = useState(preview?.islemler || []);
  const [giderler, setGiderler] = useState(preview?.giderler || []);
  const [prevIslemler, setPrevIslemler] = useState(preview?.prevIslemler || []);
  const [prevGiderler, setPrevGiderler] = useState(preview?.prevGiderler || []);
  // Maliyet ayarlari (has alis / has satis secimi) kar hesabini dogrudan etkiler
  const [costSettings, setCostSettings] = useState(preview?.settings || null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [personeller, setPersoneller] = useState(preview?.personeller || []);
  const [drawer, setDrawer] = useState({ open: false, kind: 'kar' });

  const range = useMemo(
    () => resolvePresetRange(preset, { startYmd: customStart, endYmd: customEnd }),
    [preset, customStart, customEnd]
  );
  const compareRange = useMemo(() => resolveCompareRange(preset, range), [preset, range]);

  const load = useCallback(async () => {
    if (preview) {
      setIslemler(preview.islemler || []);
      setGiderler(preview.giderler || []);
      setPrevIslemler(preview.prevIslemler || []);
      setPrevGiderler(preview.prevGiderler || []);
      setLoading(false);
      return;
    }
    if (!sube) return;
    setLoading(true);
    setError('');
    try {
      const jobs = [
        loadIslemler(sube, range),
        loadFinanceExpenses(sube)
      ];
      if (compareRange) {
        jobs.push(loadIslemler(sube, compareRange));
      }
      const [curIslem, curGider, prevIslem] = await Promise.all(jobs);
      setIslemler(curIslem);
      setGiderler(curGider);
      setPrevIslemler(prevIslem || []);
      setPrevGiderler(curGider || []);
    } catch (_err) {
      setError('Finans verisi okunamadı.');
      setIslemler([]);
      setGiderler([]);
    } finally {
      setLoading(false);
    }
  }, [sube, range, compareRange, preview]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (preview || !sube) return;
    loadFinanceCostSettings(sube)
      .then((row) => setCostSettings(row || null))
      .catch(() => setCostSettings(null));
  }, [sube, preview]);

  useEffect(() => {
    if (preview || !sube) return;
    getDocs(collection(db, 'personeller'))
      .then((snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPersoneller(rows.filter((p) => p.aktif !== false && (p.yetkiler?.subeler || []).includes(sube)));
      })
      .catch(() => setPersoneller([]));
  }, [sube, preview]);

  // KPI kartlarinda TL'nin altinda gram karsiligi icin canli has SATIS fiyati
  const hasSellTry = useMemo(() => getLiveHasRates(livePrices).sell, [livePrices]);
  const pnl = useMemo(() => summarizeFinancePeriod({ islemler, giderler, prices: livePrices, range, settings: costSettings }), [islemler, giderler, livePrices, range, costSettings]);
  const prevPnl = useMemo(() => summarizeFinancePeriod({ islemler: prevIslemler, giderler: prevGiderler, prices: livePrices, range: compareRange, settings: costSettings }), [prevIslemler, prevGiderler, livePrices, compareRange, costSettings]);
  const delta = useMemo(() => compareFinanceSummaries(pnl, prevPnl), [pnl, prevPnl]);

  const subeLabel = String(sube || '').replace(/_/g, ' ');

  const drawerRows = drawer.kind === 'gider'
    ? giderler.filter(isActiveExpense)
    : pnl.sales;

  return (
    <Box
      data-testid="finans-page"
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.15,
        bgcolor: fx.bg,
        overflow: { xs: 'auto', md: 'hidden' }
      }}
    >
      <FinancePeriodSelector
        preset={preset}
        range={range}
        subeLabel={subeLabel}
        pdfBusy={pdfBusy}
        onPdf={async () => {
          setPdfBusy(true);
          try {
            const file = await buildFinanceSummaryPdf({
              branchLabel: subeLabel,
              range,
              pnl,
              giderler,
              prices: livePrices,
              dateLabel: new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })
            });
            const url = URL.createObjectURL(file.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
          } catch (err) {
            setError(err?.message || 'PDF oluşturulamadı.');
          } finally {
            setPdfBusy(false);
          }
        }}
        customStart={customStart}
        customEnd={customEnd}
        onPreset={setPreset}
        onCustom={({ start, end }) => {
          if (start) setCustomStart(start);
          if (end) setCustomEnd(end);
        }}
      />
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        sx={{
          minHeight: 40,
          flexShrink: 0,
          borderBottom: `1px solid ${fx.border}`,
          '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2, bgcolor: '#1d4ed8' },
          '& .MuiTab-root': {
            minHeight: 40,
            py: 0,
            px: 1.75,
            fontWeight: 750,
            fontSize: 13.5,
            minWidth: 0,
            color: fx.muted,
            textTransform: 'none'
          },
          '& .Mui-selected': { color: '#1d4ed8 !important' }
        }}
      >
        <Tab value="ozet" label="Özet" />
        <Tab value="giderler" label="Giderler" />
        <Tab value="maliyet" label="Maliyet Ayarları" />
      </Tabs>
      {error ? <Alert severity="error" sx={{ py: 0, '& .MuiAlert-message': { fontSize: 12 } }}>{error}</Alert> : null}
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
      ) : tab === 'ozet' ? (
        <FinanceOverview pnl={pnl} delta={delta} giderler={giderler} range={range} hasSellTry={hasSellTry} onDetail={(kind) => setDrawer({ open: true, kind })} />
      ) : tab === 'giderler' ? (
        <FinanceExpenses
          giderler={giderler}
          range={range}
          canEdit={canEdit}
          personeller={personeller}
          loading={false}
          onAdd={async (form) => {
            if (preview) {
              setGiderler((prev) => [{ id: `p-${Date.now()}`, ...form, amountTry: Number(form.amountTry), status: 'active', period: 'monthly' }, ...prev]);
              return;
            }
            await addFinanceExpense(sube, form, activePersonel);
            await load();
          }}
          onUpdate={async (id, form) => {
            if (preview) {
              setGiderler((prev) => prev.map((g) => (g.id === id ? { ...g, ...form, amountTry: Number(form.amountTry) } : g)));
              return;
            }
            await updateFinanceExpense(sube, id, form, activePersonel);
            await load();
          }}
          onCancel={async (id) => {
            if (preview) {
              setGiderler((prev) => prev.map((g) => (g.id === id ? { ...g, status: 'cancelled' } : g)));
              return;
            }
            await cancelFinanceExpense(sube, id, activePersonel);
            await load();
          }}
        />
      ) : (
        <FinanceCostSettings sube={sube} activePersonel={activePersonel} branchAuthIsAdmin={branchAuthIsAdmin} preview={preview} />
      )}
      <FinanceDetailDrawer
        open={drawer.open}
        kind={drawer.kind === 'gider' ? 'gider' : 'islem'}
        title={drawer.kind === 'gider' ? 'Gider detayı' : 'İşlem detayı'}
        rows={drawerRows}
        empty={drawer.kind === 'gider' ? 'Bu dönemde kayıtlı gider bulunmuyor.' : 'Bu dönemde satış kaydı yok.'}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        range={range}
      />
    </Box>
  );
}
